<template>
    <div class="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div class="mb-6">
            <h1 class="text-2xl font-semibold text-gray-900">
                Impressions Inventory Reports
            </h1>

            <p class="mt-1 text-sm text-gray-600">
                Queue, monitor and download impressions inventory CSV reports.
            </p>
        </div>

        <div class="mb-8 rounded-lg bg-white p-6 shadow">
            <h2 class="mb-5 text-lg font-medium text-gray-900">
                Create report
            </h2>

            <form @submit.prevent="queueReport">
                <div class="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <div>
                        <label
                            for="start-date"
                            class="block text-sm font-medium text-gray-700"
                        >
                            Start date
                        </label>

                        <input
                            id="start-date"
                            v-model="form.start_date"
                            type="date"
                            required
                            :max="form.end_date || undefined"
                            class="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        >

                        <p
                            v-if="fieldError('start_date')"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ fieldError('start_date') }}
                        </p>
                    </div>

                    <div>
                        <label
                            for="end-date"
                            class="block text-sm font-medium text-gray-700"
                        >
                            End date
                        </label>

                        <input
                            id="end-date"
                            v-model="form.end_date"
                            type="date"
                            required
                            :min="form.start_date || undefined"
                            class="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        >

                        <p
                            v-if="fieldError('end_date')"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ fieldError('end_date') }}
                        </p>
                    </div>

                    <div>
                        <label
                            for="buffer-percentage"
                            class="block text-sm font-medium text-gray-700"
                        >
                            Buffer percentage
                        </label>

                        <div class="relative mt-1">
                            <input
                                id="buffer-percentage"
                                v-model.number="form.buffer_percentage"
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                required
                                class="block w-full rounded-md border border-gray-300 px-3 py-2 pr-10 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            >

                            <div
                                class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
                            >
                                <span class="text-gray-500">
                                    %
                                </span>
                            </div>
                        </div>

                        <p class="mt-1 text-xs text-gray-500">
                            Defaults to 20%.
                        </p>

                        <p
                            v-if="fieldError('buffer_percentage')"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ fieldError('buffer_percentage') }}
                        </p>
                    </div>

                    <div>
                        <label
                            for="minimum-view-time"
                            class="block text-sm font-medium text-gray-700"
                        >
                            Minimum view time
                        </label>

                        <div class="relative mt-1">
                            <input
                                id="minimum-view-time"
                                v-model.number="form.minimum_view_time"
                                type="number"
                                min="0"
                                step="1"
                                required
                                class="block w-full rounded-md border border-gray-300 px-3 py-2 pr-20 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            >

                            <div
                                class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
                            >
                                <span class="text-gray-500">
                                    seconds
                                </span>
                            </div>
                        </div>

                        <p class="mt-1 text-xs text-gray-500">
                            Defaults to 1 second.
                        </p>

                        <p
                            v-if="fieldError('minimum_view_time')"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ fieldError('minimum_view_time') }}
                        </p>
                    </div>

                    <!-- Games -->
                    <div class="md:col-span-2">
                        <label class="block text-sm font-medium text-gray-700">
                            Games
                        </label>

                        <div class="mt-1">
                            <DropdownComponent
                                :items="availableCampaignGames"
                                display="name"
                                selected="All Games"
                                class="w-full"
                                @user_select="addGameToFilter"
                            />
                        </div>

                        <p
                            v-if="loadingGames"
                            class="mt-1 text-xs text-gray-500"
                        >
                            Loading games...
                        </p>

                        <p
                            v-else-if="gamesError"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ gamesError }}
                        </p>

                        <div
                            v-if="game_filter_array.length"
                            class="mt-2 flex flex-row flex-wrap gap-1"
                        >
                            <button
                                v-for="game in game_filter_array"
                                :key="game.place_id"
                                type="button"
                                class="cursor-pointer rounded-md bg-white px-2 py-1 text-sm shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-blue-300"
                                @click="removeGameFromFilter(game)"
                            >
                                {{ game.name }}

                                <span class="ml-1 text-gray-500">
                                    ×
                                </span>
                            </button>
                        </div>

                        <p class="mt-1 text-xs text-gray-500">
                            Leave empty to include all games.
                        </p>

                        <p
                            v-if="fieldError('place_ids')"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ fieldError('place_ids') }}
                        </p>
                    </div>

                    <div class="md:col-span-2">
                        <label
                            for="disallowed-place-ids"
                            class="block text-sm font-medium text-gray-700"
                        >
                            Disallowed place IDs
                        </label>

                        <textarea
                            id="disallowed-place-ids"
                            v-model="form.disallowed_place_ids_csv"
                            rows="3"
                            placeholder="123456789, 987654321, 456789123"
                            class="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />

                        <p class="mt-1 text-xs text-gray-500">
                            Optional. Enter numeric place IDs separated by
                            commas, spaces or new lines.
                        </p>

                        <p
                            v-if="localErrors.disallowed_place_ids"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ localErrors.disallowed_place_ids }}
                        </p>

                        <p
                            v-else-if="fieldError('disallowed_place_ids')"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ fieldError('disallowed_place_ids') }}
                        </p>
                    </div>
                </div>

                <div
                    v-if="submitError"
                    class="mt-5 rounded-md bg-red-50 p-4"
                >
                    <p class="text-sm text-red-700">
                        {{ submitError }}
                    </p>
                </div>

                <div
                    v-if="submitSuccess"
                    class="mt-5 rounded-md bg-green-50 p-4"
                >
                    <p class="text-sm text-green-700">
                        {{ submitSuccess }}
                    </p>
                </div>

                <div class="mt-6 flex justify-end">
                    <button
                        type="submit"
                        :disabled="submitting"
                        class="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <svg
                            v-if="submitting"
                            class="-ml-1 mr-2 h-4 w-4 animate-spin"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                class="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                stroke-width="4"
                            />

                            <path
                                class="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                            />
                        </svg>

                        {{ submitting ? 'Queueing...' : 'Queue report' }}
                    </button>
                </div>
            </form>
        </div>

        <div class="overflow-hidden rounded-lg bg-white shadow">
            <div
                class="flex items-center justify-between border-b border-gray-200 px-6 py-4"
            >
                <div>
                    <h2 class="text-lg font-medium text-gray-900">
                        Reports
                    </h2>

                    <p class="mt-1 text-sm text-gray-500">
                        Pending and running reports refresh automatically.
                    </p>
                </div>

                <button
                    type="button"
                    :disabled="loadingReports"
                    class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    @click="loadReports"
                >
                    {{ loadingReports ? 'Refreshing...' : 'Refresh' }}
                </button>
            </div>

            <div
                v-if="reportsError"
                class="border-b border-red-200 bg-red-50 px-6 py-4"
            >
                <p class="text-sm text-red-700">
                    {{ reportsError }}
                </p>
            </div>

            <div
                v-if="loadingReports && reports.length === 0"
                class="px-6 py-12 text-center"
            >
                <p class="text-sm text-gray-500">
                    Loading reports...
                </p>
            </div>

            <div
                v-else-if="reports.length === 0"
                class="px-6 py-12 text-center"
            >
                <p class="text-sm text-gray-500">
                    No inventory reports have been created yet.
                </p>
            </div>

            <div v-else class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                        <tr>
                            <th :class="headerClass">
                                Date range
                            </th>

                            <th :class="headerClass">
                                Buffer
                            </th>

                            <th :class="headerClass">
                                Min. view time
                            </th>

                            <th :class="headerClass">
                                Status
                            </th>

                            <th :class="headerClass">
                                Rows
                            </th>

                            <th :class="headerClass">
                                Created
                            </th>

                            <th
                                class="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500"
                            >
                                Actions
                            </th>
                        </tr>
                    </thead>

                    <tbody class="divide-y divide-gray-200 bg-white">
                        <tr
                            v-for="report in reports"
                            :key="report.uuid"
                        >
                            <td
                                class="whitespace-nowrap px-6 py-4 text-sm text-gray-900"
                            >
                                <div>
                                    {{ formatDate(report.start_date) }}
                                </div>

                                <div class="text-xs text-gray-500">
                                    to {{ formatDate(report.end_date) }}
                                </div>
                            </td>

                            <td
                                class="whitespace-nowrap px-6 py-4 text-sm text-gray-700"
                            >
                                {{ report.buffer_percentage }}%
                            </td>

                            <td
                                class="whitespace-nowrap px-6 py-4 text-sm text-gray-700"
                            >
                                {{ report.minimum_view_time }}s
                            </td>

                            <td class="whitespace-nowrap px-6 py-4">
                                <span
                                    :class="statusClass(report.status)"
                                    class="inline-flex rounded-full px-2 text-xs font-semibold leading-5"
                                >
                                    {{ statusLabel(report.status) }}
                                </span>
                            </td>

                            <td
                                class="whitespace-nowrap px-6 py-4 text-sm text-gray-700"
                            >
                                {{
                                    report.row_count == null
                                        ? '—'
                                        : formatNumber(report.row_count)
                                }}
                            </td>

                            <td
                                class="whitespace-nowrap px-6 py-4 text-sm text-gray-700"
                            >
                                {{ formatDateTime(report.created_at) }}
                            </td>

                            <td
                                class="whitespace-nowrap px-6 py-4 text-right text-sm"
                            >
                                <button
                                    v-if="report.status === 'completed'"
                                    type="button"
                                    :disabled="
                                        downloadingUuid === report.uuid
                                    "
                                    class="font-medium text-indigo-600 hover:text-indigo-900 disabled:cursor-not-allowed disabled:opacity-50"
                                    @click="downloadReport(report)"
                                >
                                    {{
                                        downloadingUuid === report.uuid
                                            ? 'Downloading...'
                                            : 'Download'
                                    }}
                                </button>

                                <span
                                    v-else-if="
                                        report.status === 'pending'
                                        || report.status === 'running'
                                    "
                                    class="text-gray-400"
                                >
                                    Processing
                                </span>

                                <button
                                    v-else-if="report.status === 'failed'"
                                    type="button"
                                    class="font-medium text-red-600 hover:text-red-900"
                                    @click="showFailure(report)"
                                >
                                    View error
                                </button>

                                <span
                                    v-else
                                    class="text-gray-400"
                                >
                                    —
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div
                v-if="pagination.last_page > 1"
                class="flex items-center justify-between border-t border-gray-200 px-6 py-4"
            >
                <button
                    type="button"
                    :disabled="pagination.current_page <= 1"
                    class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                    @click="changePage(pagination.current_page - 1)"
                >
                    Previous
                </button>

                <span class="text-sm text-gray-600">
                    Page {{ pagination.current_page }}
                    of {{ pagination.last_page }}
                </span>

                <button
                    type="button"
                    :disabled="
                        pagination.current_page >= pagination.last_page
                    "
                    class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                    @click="changePage(pagination.current_page + 1)"
                >
                    Next
                </button>
            </div>
        </div>

        <div
            v-if="failureReport"
            class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4"
            @click.self="failureReport = null"
        >
            <div
                class="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl"
            >
                <div class="flex items-start justify-between">
                    <h3 class="text-lg font-medium text-gray-900">
                        Report failed
                    </h3>

                    <button
                        type="button"
                        class="text-gray-400 hover:text-gray-600"
                        @click="failureReport = null"
                    >
                        ×
                    </button>
                </div>

                <p class="mt-4 break-words text-sm text-red-700">
                    {{
                        failureReport.error_message
                        || 'No error message was recorded.'
                    }}
                </p>

                <div class="mt-6 flex justify-end">
                    <button
                        type="button"
                        class="rounded-md bg-gray-800 px-4 py-2 text-sm font-medium text-white hover:bg-gray-900"
                        @click="failureReport = null"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

const DEFAULT_FORM = () => ({
    start_date: '',
    end_date: '',
    buffer_percentage: 20,
    minimum_view_time: 1,
    disallowed_place_ids_csv: '',
});

export default {
    name: 'ImpressionsInventoryExports',

    data() {
        return {
            form: DEFAULT_FORM(),

            all_campaign_games: [],
            game_filter_array: [],

            reports: [],

            pagination: {
                current_page: 1,
                last_page: 1,
                per_page: 20,
                total: 0,
            },

            validationErrors: {},
            localErrors: {},

            submitting: false,
            loadingReports: false,
            loadingGames: false,
            downloadingUuid: null,

            submitError: '',
            submitSuccess: '',
            reportsError: '',
            gamesError: '',

            failureReport: null,
            pollingTimer: null,

            headerClass:
                'px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500',
        };
    },

    computed: {
        selectedPlaceIds() {
            return [
                ...new Set(
                    this.game_filter_array
                        .map((game) => Number(game.place_id))
                        .filter((placeId) => {
                            return Number.isInteger(placeId)
                                && placeId > 0;
                        })
                ),
            ];
        },

        availableCampaignGames() {
            const selectedPlaceIds = new Set(
                this.selectedPlaceIds
            );

            return this.all_campaign_games.filter((game) => {
                return !selectedPlaceIds.has(
                    Number(game.place_id)
                );
            });
        },

        hasProcessingReports() {
            return this.reports.some((report) => {
                return report.status === 'pending'
                    || report.status === 'running';
            });
        },
    },

    watch: {
        hasProcessingReports(shouldPoll) {
            if (shouldPoll) {
                this.startPolling();
                return;
            }

            this.stopPolling();
        },
    },

    created() {
        this.loadGames();
        this.loadReports();
    },

    beforeUnmount() {
        this.stopPolling();
    },

    methods: {
        async loadGames() {
            if (this.loadingGames) {
                return;
            }

            this.loadingGames = true;
            this.gamesError = '';

            try {
                const response = await axios.get('/games/all');

                const games = Array.isArray(response.data)
                    ? response.data
                    : response.data?.data;

                if (!Array.isArray(games)) {
                    throw new Error(
                        'Invalid games response.'
                    );
                }

                this.all_campaign_games = games
                    .map((game) => {
                        const placeId = Number(
                            game.place_id ?? game.placeId
                        );

                        return {
                            ...game,
                            place_id: placeId,
                            name:
                                game.friendly_name
                                || game.name
                                || String(placeId),
                        };
                    })
                    .filter((game) => {
                        return Number.isInteger(game.place_id)
                            && game.place_id > 0;
                    })
                    .sort((firstGame, secondGame) => {
                        return firstGame.name.localeCompare(
                            secondGame.name
                        );
                    });
            } catch (error) {
                this.all_campaign_games = [];

                this.gamesError =
                    error.response?.data?.message
                    || 'Unable to load games.';
            } finally {
                this.loadingGames = false;
            }
        },

        addGameToFilter(game) {
            if (!game) {
                return;
            }

            const placeId = Number(game.place_id);

            if (
                !Number.isInteger(placeId)
                || placeId <= 0
            ) {
                return;
            }

            if (this.selectedPlaceIds.includes(placeId)) {
                return;
            }

            this.game_filter_array = [
                ...this.game_filter_array,
                game,
            ];
        },

        removeGameFromFilter(game) {
            const placeId = Number(game.place_id);

            this.game_filter_array =
                this.game_filter_array.filter(
                    (selectedGame) => {
                        return Number(
                            selectedGame.place_id
                        ) !== placeId;
                    }
                );
        },

        async queueReport() {
            this.clearSubmissionMessages();

            const disallowedPlaceIds =
                this.parseDisallowedPlaceIds();

            if (disallowedPlaceIds === null) {
                return;
            }

            if (
                this.form.start_date
                && this.form.end_date
                && this.form.start_date > this.form.end_date
            ) {
                this.localErrors = {
                    ...this.localErrors,
                    end_date:
                        'The end date must be on or after the start date.',
                };

                return;
            }

            const overlappingPlaceIds =
                this.selectedPlaceIds.filter((placeId) => {
                    return disallowedPlaceIds.includes(placeId);
                });

            if (overlappingPlaceIds.length > 0) {
                this.localErrors = {
                    ...this.localErrors,
                    disallowed_place_ids:
                        'A selected game cannot also be disallowed.',
                };

                return;
            }

            this.submitting = true;

            try {
                const response = await axios.post(
                    '/impressions-inventory-exports',
                    {
                        start_date: this.form.start_date,
                        end_date: this.form.end_date,
                        buffer_percentage:
                            Number(
                                this.form.buffer_percentage
                            ),
                        minimum_view_time:
                            Number(
                                this.form.minimum_view_time
                            ),
                        place_ids:
                            this.selectedPlaceIds,
                        disallowed_place_ids:
                            disallowedPlaceIds,
                    }
                );

                this.submitSuccess =
                    response.data.message
                    || 'The report has been queued successfully.';

                this.resetForm();

                await this.loadReports(1);
            } catch (error) {
                this.handleSubmitError(error);
            } finally {
                this.submitting = false;
            }
        },

        async loadReports(page = null) {
            if (this.loadingReports) {
                return;
            }

            this.loadingReports = true;
            this.reportsError = '';

            const requestedPage =
                page
                || this.pagination.current_page
                || 1;

            try {
                const response = await axios.get(
                    '/impressions-inventory-exports',
                    {
                        params: {
                            page: requestedPage,
                            per_page:
                                this.pagination.per_page,
                        },
                    }
                );

                const payload = response.data;

                this.reports = Array.isArray(payload.data)
                    ? payload.data
                    : [];

                this.pagination = {
                    current_page:
                        Number(payload.current_page) || 1,
                    last_page:
                        Number(payload.last_page) || 1,
                    per_page:
                        Number(payload.per_page) || 20,
                    total:
                        Number(payload.total) || 0,
                    from:
                        payload.from ?? null,
                    to:
                        payload.to ?? null,
                };
            } catch (error) {
                this.reportsError =
                    error.response?.data?.message
                    || 'Unable to load the inventory reports.';
            } finally {
                this.loadingReports = false;
            }
        },

        async downloadReport(report) {
            this.downloadingUuid = report.uuid;

            try {
                const response = await axios.get(
                    `/impressions-inventory-exports/${report.uuid}/download`,
                    {
                        responseType: 'blob',
                    }
                );

                const blob = new Blob(
                    [response.data],
                    {
                        type:
                            response.headers['content-type']
                            || 'text/csv',
                    }
                );

                const objectUrl =
                    window.URL.createObjectURL(blob);

                const link =
                    document.createElement('a');

                link.href = objectUrl;

                link.download =
                    report.filename
                    || `impressions-inventory-${report.uuid}.csv`;

                document.body.appendChild(link);

                link.click();
                link.remove();

                window.URL.revokeObjectURL(objectUrl);
            } catch (error) {
                const message =
                    await this.extractBlobError(error);

                this.reportsError =
                    message
                    || 'Unable to download this report.';
            } finally {
                this.downloadingUuid = null;
            }
        },

        parseDisallowedPlaceIds() {
            this.localErrors = {};

            const raw =
                this.form.disallowed_place_ids_csv.trim();

            if (!raw) {
                return [];
            }

            const values = raw
                .split(/[\s,]+/)
                .map((value) => value.trim())
                .filter(Boolean);

            const invalidValues =
                values.filter((value) => {
                    return !/^\d+$/.test(value)
                        || Number(value) <= 0;
                });

            if (invalidValues.length > 0) {
                this.localErrors = {
                    disallowed_place_ids:
                        `Invalid place IDs: ${invalidValues.join(', ')}`,
                };

                return null;
            }

            return [
                ...new Set(
                    values.map((value) => Number(value))
                ),
            ];
        },

        handleSubmitError(error) {
            if (error.response?.status === 422) {
                this.validationErrors =
                    error.response.data.errors || {};

                this.submitError =
                    error.response.data.message
                    || 'Please correct the highlighted fields.';

                return;
            }

            this.submitError =
                error.response?.data?.message
                || 'Unable to queue the report.';
        },

        fieldError(field) {
            if (this.localErrors[field]) {
                return this.localErrors[field];
            }

            const errors =
                this.validationErrors[field];

            if (!errors) {
                return '';
            }

            return Array.isArray(errors)
                ? errors[0]
                : errors;
        },

        clearSubmissionMessages() {
            this.validationErrors = {};
            this.localErrors = {};
            this.submitError = '';
            this.submitSuccess = '';
        },

        resetForm() {
            this.form = DEFAULT_FORM();
            this.game_filter_array = [];
            this.validationErrors = {};
            this.localErrors = {};
        },

        startPolling() {
            if (this.pollingTimer) {
                return;
            }

            this.pollingTimer =
                window.setInterval(() => {
                    this.loadReports();
                }, 5000);
        },

        stopPolling() {
            if (!this.pollingTimer) {
                return;
            }

            window.clearInterval(this.pollingTimer);

            this.pollingTimer = null;
        },

        changePage(page) {
            if (
                page < 1
                || page > this.pagination.last_page
            ) {
                return;
            }

            this.loadReports(page);
        },

        showFailure(report) {
            this.failureReport = report;
        },

        statusLabel(status) {
            const labels = {
                pending: 'Pending',
                running: 'Running',
                completed: 'Completed',
                failed: 'Failed',
            };

            return labels[status]
                || status
                || 'Unknown';
        },

        statusClass(status) {
            const classes = {
                pending:
                    'bg-yellow-100 text-yellow-800',
                running:
                    'bg-blue-100 text-blue-800',
                completed:
                    'bg-green-100 text-green-800',
                failed:
                    'bg-red-100 text-red-800',
            };

            return classes[status]
                || 'bg-gray-100 text-gray-800';
        },

        formatNumber(value) {
            return new Intl.NumberFormat('en-GB')
                .format(Number(value));
        },

        formatDate(value) {
            if (!value) {
                return '—';
            }

            const datePart =
                String(value).substring(0, 10);

            const [
                year,
                month,
                day,
            ] = datePart
                .split('-')
                .map(Number);

            if (!year || !month || !day) {
                return value;
            }

            return new Intl.DateTimeFormat(
                'en-GB',
                {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    timeZone: 'UTC',
                }
            ).format(
                new Date(
                    Date.UTC(
                        year,
                        month - 1,
                        day
                    )
                )
            );
        },

        formatDateTime(value) {
            if (!value) {
                return '—';
            }

            return new Intl.DateTimeFormat(
                'en-GB',
                {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                }
            ).format(new Date(value));
        },

        async extractBlobError(error) {
            const data =
                error.response?.data;

            if (!(data instanceof Blob)) {
                return error.response?.data?.message
                    || '';
            }

            try {
                const text = await data.text();
                const parsed = JSON.parse(text);

                return parsed.message
                    || parsed.error
                    || '';
            } catch (exception) {
                return '';
            }
        },
    },
};
</script>